/** EmployeeProfileProvider — one mounted profile dialog + hook to open it from
 * anywhere in the app. Also exports <EmployeeName /> — a clickable name that
 * opens the profile popup.
 */
import { createContext, useCallback, useContext, useState } from "react";
import EmployeeProfileDialog from "@/components/employee/EmployeeProfileDialog";

const Ctx = createContext({ open: () => {} });

export function EmployeeProfileProvider({ children }) {
  const [uid, setUid] = useState(null);
  const [open, setOpen] = useState(false);

  const openProfile = useCallback((userId) => {
    if (!userId) return;
    setUid(userId);
    setOpen(true);
  }, []);

  const onChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(() => setUid(null), 200);
  };

  return (
    <Ctx.Provider value={{ open: openProfile }}>
      {children}
      <EmployeeProfileDialog userId={uid} open={open} onOpenChange={onChange} />
    </Ctx.Provider>
  );
}

export function useEmployeeProfile() {
  return useContext(Ctx);
}

/** <EmployeeName /> — clickable employee name that opens the profile popup.
 *  Falls back to a plain span when userId is missing. */
export function EmployeeName({ userId, name, className = "", children, testid }) {
  const { open } = useEmployeeProfile();
  if (!userId) return <span className={className}>{children || name || "—"}</span>;
  return (
    <button
      type="button"
      className={`text-left cursor-pointer underline decoration-dotted decoration-foreground/30 underline-offset-2 hover:decoration-solid ${className}`}
      onClick={(e) => { e.stopPropagation(); open(userId); }}
      data-testid={testid || `emp-name-${userId}`}
    >
      {children || name || "—"}
    </button>
  );
}
